import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useTheme } from '../lib/theme/index.js';
import { getSpellingBeeWords, type Category, type Level } from '../data/spellingBeeWords';

// Simplified type for speech recognition to avoid esbuild issues
type SpeechRecognitionType = any;


export default function SpellingBeePage() {
  const { themeVariant } = useTheme();
  const [words, setWords] = useState<string[]>([]);
  const [previousWordOrder, setPreviousWordOrder] = useState<string[]>([]); // Track previous order
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [wordList, setWordList] = useState('');
  const [answer, setAnswer] = useState('');
  const [result, setResult] = useState<{ isCorrect: boolean; message: string } | null>(null);
  const [showSetup, setShowSetup] = useState(true);
  const [recognizing, setRecognizing] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState('');
  const recognitionRef = useRef<SpeechRecognitionType | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<Level | null>(null);
  const [selectedQuestionCount, setSelectedQuestionCount] = useState<number | 'all' | null>(null);
  const [loadingWords, setLoadingWords] = useState(false);
  const [keepVoiceOn, setKeepVoiceOn] = useState(false);
  const [voicePreview, setVoicePreview] = useState<string | null>(null);
  const [showFinalScore, setShowFinalScore] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [totalWordsAvailable, setTotalWordsAvailable] = useState(0);

  // Refs for keyboard navigation
  const playWordButtonRef = useRef<HTMLButtonElement>(null);
  const answerInputRef = useRef<HTMLInputElement>(null);
  const checkButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);

  const getCurrentWord = useCallback(() => words[currentIndex] || '', [words, currentIndex]);

  const speak = useCallback((text: string) => {
    if (!text) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9;
    utterance.pitch = 1;
    window.speechSynthesis.speak(utterance);
  }, []);

  const checkSpelling = useCallback((inputValue: string, correctValue: string, fromVoice = false) => {
    if (!correctValue) return;
    const isCorrect = inputValue === correctValue;
    if (isCorrect) {
      setScore(prev => prev + 1);
    }
    setResult({
      isCorrect,
      message: isCorrect
        ? `${fromVoice ? 'Correct (voice)! ' : 'Correct! '}The word is: ${correctValue}`
        : `Not quite. The correct spelling is: ${correctValue}`
    });
    // Disable input after checking to prevent cheating
    // User can only see the result and move to next word
  }, []);

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition() as SpeechRecognitionType;
      recognition.lang = 'en-US';
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        setRecognizing(true);
        setVoiceStatus('Listening... Say the letters.');
      };

      recognition.onerror = () => {
        setRecognizing(false);
        setVoiceStatus('There was an error with voice input. Try again or use typing.');
      };

      recognition.onend = () => {
        setRecognizing(false);
        // Auto-restart if keepVoiceOn is enabled
        if (keepVoiceOn && recognitionRef.current) {
          setTimeout(() => {
            if (recognitionRef.current && keepVoiceOn) {
              try {
                recognitionRef.current.start();
              } catch (e) {
                // Ignore errors if already starting
              }
            }
          }, 100);
        } else {
          setVoiceStatus(prev => {
            if (!prev.includes('They said:')) {
              return 'Stopped listening.';
            }
            return prev;
          });
        }
      };

      recognition.onresult = (event: any) => {
        // Prevent voice input if they've already submitted an answer
        if (result) {
          return;
        }
        const transcript = event.results[0][0].transcript;
        const cleaned = transcript.replace(/\s+/g, '').toLowerCase();
        setVoiceStatus(`They said: "${transcript}" → recognized as "${cleaned}"`);
        // Show preview instead of auto-checking
        setVoicePreview(cleaned);
      };

      recognitionRef.current = recognition;
    }
  }, [words, currentIndex, checkSpelling, keepVoiceOn]);

  const loadWordsFromFile = async (category: Category, level: Level, questionCount?: number | 'all' | null) => {
    setLoadingWords(true);
    try {
      // Get words from pre-parsed TypeScript data
      const extractedWords = getSpellingBeeWords(category, level);
      
      if (extractedWords.length === 0) {
        throw new Error('No words found in selected category and level');
      }
      
      // Store total available words for "All" label
      setTotalWordsAvailable(extractedWords.length);
      
      // Always shuffle words for practice - ensure different order than before
      const shuffled = shuffleArray(extractedWords, previousWordOrder);
      
      // Use provided questionCount or fall back to state
      const countToUse = questionCount !== undefined ? questionCount : selectedQuestionCount;
      
      // Limit to selected question count (must have a selection)
      if (countToUse === null) {
        throw new Error('Please select a word count first');
      }
      
      const limitedWords = countToUse === 'all'
        ? shuffled 
        : shuffled.slice(0, countToUse);
      
      setWords(limitedWords);
      setTotalQuestions(limitedWords.length);
      setPreviousWordOrder(shuffled); // Store full order for next time
      setCurrentIndex(0);
      setScore(0);
      setShowSetup(false);
      setShowFinalScore(false);
      setAnswer('');
      setResult(null);
      setVoiceStatus('');
      setVoicePreview(null);
      
      // Focus Play Word button when words are loaded
      setTimeout(() => {
        playWordButtonRef.current?.focus();
      }, 100);
    } catch (error) {
      console.error('Error loading words:', error);
      alert('Could not load words. Please try using the manual paste option below.');
    } finally {
      setLoadingWords(false);
    }
  };

  const handleCategorySelect = (category: Category) => {
    setSelectedCategory(category);
    setSelectedLevel(null);
  };

  const handleLevelSelect = (level: Level) => {
    setSelectedLevel(level);
    // Get total word count for "All" label display
    if (selectedCategory) {
      const allWords = getSpellingBeeWords(selectedCategory, level);
      setTotalWordsAvailable(allWords.length);
    }
    // Don't load words yet - wait for question count selection
  };

  const handleQuestionCountSelect = (count: number | 'all') => {
    setSelectedQuestionCount(count);
    if (selectedCategory && selectedLevel) {
      // Get total count first to show in "All" label
      const allWords = getSpellingBeeWords(selectedCategory, selectedLevel);
      setTotalWordsAvailable(allWords.length);
      // Pass count directly to avoid stale state issues
      loadWordsFromFile(selectedCategory, selectedLevel, count);
    }
  };

  // Shuffle array function - ensures different order and no consecutive duplicates
  const shuffleArray = <T,>(array: T[], previousOrder?: T[]): T[] => {
    if (array.length === 0) return [];
    
    let shuffled: T[];
    let attempts = 0;
    const maxAttempts = 100; // Prevent infinite loop
    
    do {
      shuffled = [...array];
      // Fisher-Yates shuffle
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      attempts++;
      
      // Check if order is different from previous (if provided)
      const isDifferent = !previousOrder || 
        shuffled.length !== previousOrder.length ||
        shuffled.some((val, idx) => val !== previousOrder[idx]);
      
      // Check for no consecutive duplicates
      const hasConsecutiveDuplicates = shuffled.some((val, idx) => 
        idx > 0 && val === shuffled[idx - 1]
      );
      
      // If order is different and no consecutive duplicates, we're good
      if (isDifferent && !hasConsecutiveDuplicates) {
        break;
      }
      
      // If we've tried too many times, just return what we have
      if (attempts >= maxAttempts) {
        break;
      }
    } while (attempts < maxAttempts);
    
    return shuffled;
  };

  const handleStart = () => {
    const raw = wordList
      .split(/\r?\n/)
      .map(w => w.trim())
      .filter(w => w.length > 0);

    if (!raw.length) {
      alert('Please paste at least one word to start.');
      return;
    }

    // Always shuffle words for practice - ensure different order than before
    const shuffled = shuffleArray(raw, previousWordOrder);
    setWords(shuffled);
    setPreviousWordOrder(shuffled); // Store this order for next time
    setCurrentIndex(0);
    setScore(0);
    setShowSetup(false);
    setAnswer('');
    setResult(null);
    setVoiceStatus('');
    setVoicePreview(null);
    
    // Focus Play Word button when words are loaded
    setTimeout(() => {
      playWordButtonRef.current?.focus();
    }, 100);
  };

  const handleCheck = () => {
    const typed = answer.trim().toLowerCase();
    if (!typed) {
      alert('Type a spelling first, or use voice spelling.');
      return;
    }
    const correct = getCurrentWord().toLowerCase();
    checkSpelling(typed, correct, false);
    
    // If this is the last word, automatically show final score after a short delay
    if (currentIndex >= words.length - 1) {
      setTimeout(() => {
        setShowFinalScore(true);
        // Stop voice recognition when done
        if (recognitionRef.current && recognizing) {
          recognitionRef.current.stop();
          setKeepVoiceOn(false);
        }
      }, 1500); // Show results after 1.5 seconds so they can see the result
    } else {
      // After checking, focus Next Word button
      setTimeout(() => {
        nextButtonRef.current?.focus();
      }, 100);
    }
  };

  const handleNext = () => {
    // If we're on the last word and have a result, show final score
    if (currentIndex >= words.length - 1 && result) {
      // Show final score screen
      setShowFinalScore(true);
      // Stop voice recognition when done
      if (recognitionRef.current && recognizing) {
        recognitionRef.current.stop();
        setKeepVoiceOn(false);
      }
      return;
    }
    setCurrentIndex(prev => prev + 1);
    setAnswer('');
    setResult(null);
    setVoiceStatus('');
    setVoicePreview(null);
    
    // Focus back to Play Word button for next word
    setTimeout(() => {
      playWordButtonRef.current?.focus();
    }, 100);
    
    // If keepVoiceOn is enabled, ensure recognition continues
    if (keepVoiceOn && recognitionRef.current && !recognizing) {
      setTimeout(() => {
        if (recognitionRef.current && keepVoiceOn) {
          try {
            recognitionRef.current.start();
          } catch (e) {
            // Ignore errors
          }
        }
      }, 200);
    }
  };

  const handleVoiceClick = () => {
    if (!recognitionRef.current) {
      alert('Voice spelling is not supported in this browser. Try using Chrome on a computer.');
      return;
    }
    if (recognizing) {
      recognitionRef.current.stop();
      setKeepVoiceOn(false);
      return;
    }
    recognitionRef.current.start();
  };

  const handleKeepVoiceOnChange = (checked: boolean) => {
    setKeepVoiceOn(checked);
    if (checked && recognitionRef.current && !recognizing) {
      // Start recognition if checkbox is checked and not already listening
      recognitionRef.current.start();
    } else if (!checked && recognizing && recognitionRef.current) {
      // Stop recognition if checkbox is unchecked
      recognitionRef.current.stop();
    }
  };

  const handlePlayWord = () => {
    speak(getCurrentWord());
    // After playing, focus the input field for typing
    setTimeout(() => {
      answerInputRef.current?.focus();
    }, 100);
  };

  const handlePracticeAgain = () => {
    // Reset everything and go back to setup
    setShowSetup(true);
    setShowFinalScore(false);
    setSelectedCategory(null);
    setSelectedLevel(null);
    setSelectedQuestionCount(null);
    setWords([]);
    setCurrentIndex(0);
    setScore(0);
    setAnswer('');
    setResult(null);
    setVoiceStatus('');
    setVoicePreview(null);
    setPreviousWordOrder([]);
  };

  const isEndOfList = currentIndex >= words.length - 1 && result?.isCorrect && showFinalScore;

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden" data-theme={themeVariant}>
      <SEO 
        title="Spelling Bee Practice - Free Tool for Kids | CampWhiz"
        description="Help your child practice spelling with audio pronunciation and voice recognition. Perfect for spelling bee preparation!"
        url="/parent-tools/spelling-bee"
      />
      
      {/* Subtle yellow honeycomb background - focus on shape */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Soft yellow gradient base */}
        <div 
          className="absolute inset-0 opacity-40"
          style={{
            background: 'radial-gradient(circle at center, rgb(254 249 195), rgb(254 240 138), rgb(255 251 235))'
          }}
        ></div>
        
        {/* Clear hexagonal honeycomb pattern - larger, more defined shapes */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `
              repeating-linear-gradient(0deg, transparent, transparent 100px, rgba(255, 255, 255, 0.3) 100px, rgba(255, 255, 255, 0.3) 101px),
              repeating-linear-gradient(60deg, transparent, transparent 100px, rgba(255, 255, 255, 0.3) 100px, rgba(255, 255, 255, 0.3) 101px),
              repeating-linear-gradient(120deg, transparent, transparent 100px, rgba(255, 255, 255, 0.3) 100px, rgba(255, 255, 255, 0.3) 101px)
            `,
            backgroundSize: '200px 173px',
            backgroundPosition: '0 0, 100px 0, 100px 86px',
            opacity: 0.6,
            animation: 'hexZoom 8s ease-in-out infinite'
          }}
        ></div>
        
        {/* Subtle inner hexagon definition for 3D effect */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `
              repeating-linear-gradient(0deg, transparent, transparent 100px, rgba(0, 0, 0, 0.05) 100px, rgba(0, 0, 0, 0.05) 102px),
              repeating-linear-gradient(60deg, transparent, transparent 100px, rgba(0, 0, 0, 0.05) 100px, rgba(0, 0, 0, 0.05) 102px),
              repeating-linear-gradient(120deg, transparent, transparent 100px, rgba(0, 0, 0, 0.05) 100px, rgba(0, 0, 0, 0.05) 102px)
            `,
            backgroundSize: '200px 173px',
            backgroundPosition: '0 0, 100px 0, 100px 86px',
            opacity: 0.4,
            animation: 'hexZoom 8s ease-in-out infinite',
            animationDelay: '0.4s'
          }}
        ></div>
        
        <style>{`
          @keyframes hexZoom {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
        `}</style>
      </div>
      
      {/* Minimal header */}
      <div className="bg-white border-b border-gray-200 relative z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link 
              to="/parent-tools" 
              className="text-sm text-gray-600 hover:text-primary transition-colors"
            >
              ← Back to Parent Tools
            </Link>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">by</span>
              <a 
                href="/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block hover:opacity-80 transition-opacity"
              >
                <img 
                  src={themeVariant === 'teal' ? '/assets/campwhiz-logo-color.svg' : '/assets/campwhiz-logo-color2.svg'}
                  alt="CampWhiz"
                  className="h-[30px]"
                />
              </a>
            </div>
          </div>
        </div>
      </div>

      <main className="container mx-auto px-4 py-8 relative z-10">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 relative">
            <h1 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-8">
              🐝 Spelling Bee Practice
            </h1>

            {showSetup ? (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Choose Your Word List</h2>
                
                {/* Category Selection */}
                {!selectedCategory && (
                  <div className="mb-6">
                    <label className="block font-semibold text-gray-700 mb-3">
                      Select a category:
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => handleCategorySelect('school')}
                        className="p-4 rounded-lg border-2 border-gray-300 hover:border-primary hover:bg-primary/5 transition-all text-left"
                      >
                        <div className="font-semibold text-gray-900 mb-1">School Spelling Bee Study List</div>
                        <div className="text-sm text-gray-600">For classroom and school-level bees</div>
                      </button>
                      <button
                        onClick={() => handleCategorySelect('champions')}
                        className="p-4 rounded-lg border-2 border-gray-300 hover:border-primary hover:bg-primary/5 transition-all text-left"
                      >
                        <div className="font-semibold text-gray-900 mb-1">Words of the Champions</div>
                        <div className="text-sm text-gray-600">For advanced competition</div>
                      </button>
                    </div>
                  </div>
                )}

                {/* Level Selection */}
                {selectedCategory && !selectedLevel && (
                  <div className="mb-6">
                    <button
                      onClick={() => setSelectedCategory(null)}
                      className="text-sm text-gray-600 hover:text-primary mb-4 inline-flex items-center"
                    >
                      ← Back
                    </button>
                    <label className="block font-semibold text-gray-700 mb-3">
                      Select difficulty level:
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      <button
                        onClick={() => handleLevelSelect('one')}
                        disabled={loadingWords}
                        className="p-4 rounded-lg border-2 border-gray-300 hover:border-primary hover:bg-primary/5 transition-all disabled:opacity-50"
                      >
                        <div className="font-semibold text-gray-900 mb-1">One Bee</div>
                        <div className="text-xs text-gray-600">Easiest</div>
                      </button>
                      <button
                        onClick={() => handleLevelSelect('two')}
                        disabled={loadingWords}
                        className="p-4 rounded-lg border-2 border-gray-300 hover:border-primary hover:bg-primary/5 transition-all disabled:opacity-50"
                      >
                        <div className="font-semibold text-gray-900 mb-1">Two Bee</div>
                        <div className="text-xs text-gray-600">Medium</div>
                      </button>
                      <button
                        onClick={() => handleLevelSelect('three')}
                        disabled={loadingWords}
                        className="p-4 rounded-lg border-2 border-gray-300 hover:border-primary hover:bg-primary/5 transition-all disabled:opacity-50"
                      >
                        <div className="font-semibold text-gray-900 mb-1">Three Bee</div>
                        <div className="text-xs text-gray-600">Hardest</div>
                      </button>
                    </div>
                  </div>
                )}

                {/* Question Count Selection */}
                {selectedCategory && selectedLevel && !loadingWords && (
                  <div className="mb-6">
                    <button
                      onClick={() => setSelectedLevel(null)}
                      className="text-sm text-gray-600 hover:text-primary mb-4 inline-flex items-center"
                    >
                      ← Back
                    </button>
                    <label className="block font-semibold text-gray-700 mb-3">
                      # of Words:
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {[5, 10, 15, 25, 50, 100, 'all'].map((count) => (
                        <button
                          key={count}
                          onClick={() => handleQuestionCountSelect(count as number | 'all')}
                          disabled={loadingWords}
                          className={`p-3 rounded-lg border-2 transition-all disabled:opacity-50 ${
                            selectedQuestionCount === count
                              ? 'border-primary bg-primary/10 text-primary font-semibold'
                              : 'border-gray-300 hover:border-primary hover:bg-primary/5 text-gray-900'
                          }`}
                        >
                          {count === 'all' ? `All (${totalWordsAvailable})` : count}
                        </button>
                      ))}
                    </div>
                    {loadingWords && (
                      <div className="mt-4 text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
                        <p className="text-sm text-gray-600">Loading words...</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Divider */}
                <div className="my-8 flex items-center">
                  <div className="flex-1 border-t border-gray-300"></div>
                  <span className="px-4 text-sm text-gray-500">OR</span>
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>

                {/* Manual Paste Option */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Paste Your Own Word List</h3>
                  <label htmlFor="wordList" className="block font-semibold text-gray-700 mb-2">
                    Words (one per line)
                  </label>
                  <textarea
                    id="wordList"
                    value={wordList}
                    onChange={(e) => setWordList(e.target.value)}
                    placeholder="example&#10;because&#10;interesting&#10;practice"
                    className="w-full min-h-[150px] p-3 rounded-lg border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 resize-y"
                  />
                  <div className="mt-4">
                    <button
                      onClick={handleStart}
                      disabled={!wordList.trim()}
                      className="bg-primary text-white px-6 py-3 rounded-full font-medium hover:bg-primary/90 transition-colors shadow-md hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Start Practice
                    </button>
                  </div>
                  <p className="mt-4 text-sm text-gray-500">
                    Tip: Keep one word per line. You can paste your own word list here.
                  </p>
                </div>
              </div>
            ) : showFinalScore ? (
              <div className="text-center">
                <div className="mb-8">
                  <div className="text-7xl mb-4">
                    {totalQuestions > 0 && (score / totalQuestions) >= 0.9 ? '🏆' :
                     totalQuestions > 0 && (score / totalQuestions) >= 0.7 ? '⭐' :
                     totalQuestions > 0 && (score / totalQuestions) >= 0.5 ? '👍' : '💪'}
                  </div>
                  <h2 className="text-4xl font-bold text-gray-900 mb-2">
                    {totalQuestions > 0 && (score / totalQuestions) >= 0.9 ? 'Outstanding!' :
                     totalQuestions > 0 && (score / totalQuestions) >= 0.7 ? 'Great Job!' :
                     totalQuestions > 0 && (score / totalQuestions) >= 0.5 ? 'Well Done!' : 'Keep Practicing!'}
                  </h2>
                  <p className="text-lg text-gray-600 mb-6">
                    {totalQuestions > 0 && (score / totalQuestions) >= 0.9 ? 'You\'re a spelling champion! 🌟' :
                     totalQuestions > 0 && (score / totalQuestions) >= 0.7 ? 'You\'re doing great! Keep it up!' :
                     totalQuestions > 0 && (score / totalQuestions) >= 0.5 ? 'Good effort! Practice makes perfect!' : 'Every mistake is a chance to learn! You\'ve got this!'}
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-8 mb-8 max-w-md mx-auto">
                  <div className="text-6xl font-bold text-primary mb-2">{score}</div>
                  <div className="text-2xl text-gray-700 mb-4">out of {totalQuestions}</div>
                  {totalQuestions > 0 && (
                    <div className="text-3xl font-bold text-primary mb-4">
                      {Math.round((score / totalQuestions) * 100)}% correct
                    </div>
                  )}
                  
                  {/* Progress bar */}
                  {totalQuestions > 0 && (
                    <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                      <div 
                        className="bg-primary h-4 rounded-full transition-all duration-500"
                        style={{ width: `${(score / totalQuestions) * 100}%` }}
                      ></div>
                    </div>
                  )}
                </div>
                
                <button
                  onClick={handlePracticeAgain}
                  className="bg-primary text-white px-10 py-4 rounded-full font-semibold text-lg hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 transform"
                >
                  🐝 Practice Again
                </button>
              </div>
            ) : (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Step 2: Practice</h2>

                <div className="flex justify-between items-center mb-6 text-sm text-gray-600">
                  <div>
                    Word <span className="font-semibold">{currentIndex + 1}</span> of <span className="font-semibold">{words.length}</span>
                  </div>
                  <div>
                    Score: <span className="font-semibold text-primary">{score}</span>
                  </div>
                </div>

                <div className="mb-6">
                  <button
                    ref={playWordButtonRef}
                    onClick={handlePlayWord}
                    disabled={isEndOfList}
                    className="bg-primary text-white px-5 py-2.5 rounded-full font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    🔊 Play Word
                  </button>
                </div>
                <p className="text-sm text-gray-500 mb-6">Listen, then spell the word.</p>

                <div className="mb-6">
                  <label htmlFor="answer" className="block font-semibold text-gray-700 mb-2">
                    Type the spelling:
                  </label>
                  <input
                    ref={answerInputRef}
                    id="answer"
                    type="text"
                    value={answer}
                    onChange={(e) => {
                      // Prevent typing once they've submitted an answer
                      if (!result) {
                        setAnswer(e.target.value);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !result && answer.trim()) {
                        handleCheck();
                      }
                    }}
                    autoComplete="off"
                    className="w-full p-3 rounded-lg border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    disabled={!!result}
                    placeholder={result ? 'Answer submitted - see result below' : 'Type the spelling here'}
                  />
                  <div className="mt-3">
                    <button
                      ref={checkButtonRef}
                      onClick={handleCheck}
                      disabled={!!result || isEndOfList || !answer.trim()}
                      className="bg-primary text-white px-5 py-2.5 rounded-full font-medium hover:bg-primary/90 transition-colors mr-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Check
                    </button>
                    <button
                      ref={nextButtonRef}
                      onClick={handleNext}
                      disabled={!result}
                      className="bg-gray-200 text-gray-900 px-5 py-2.5 rounded-full font-medium hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {currentIndex >= words.length - 1 ? 'See Results' : 'Next Word'}
                    </button>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block font-semibold text-gray-700 mb-2">
                    🎙 Or try voice spelling <span className="inline-block px-2.5 py-1 rounded-full text-xs bg-blue-100 text-blue-700 ml-2">Beta</span>
                  </label>
                  <div className="flex items-center gap-3 mb-2">
                    <button
                      onClick={handleVoiceClick}
                      disabled={isEndOfList || !recognitionRef.current}
                      className="bg-gray-200 text-gray-900 px-5 py-2.5 rounded-full font-medium hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      🎙 {recognizing ? 'Stop Listening' : 'Try Voice Spelling (Beta)'}
                    </button>
                    {recognitionRef.current && (
                      <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={keepVoiceOn}
                          onChange={(e) => handleKeepVoiceOnChange(e.target.checked)}
                          disabled={isEndOfList}
                          className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary focus:ring-2"
                        />
                        <span>Keep voice on</span>
                      </label>
                    )}
                  </div>
                  {voiceStatus && (
                    <div className="mt-2 text-sm text-gray-600">
                      {voiceStatus}
                    </div>
                  )}
                  {voicePreview && !result && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="text-sm text-gray-700 mb-2">
                        Preview: <span className="font-semibold text-gray-900">{voicePreview}</span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            const correct = getCurrentWord().toLowerCase();
                            checkSpelling(voicePreview, correct, true);
                            setVoicePreview(null);
                            
                            // If this is the last word, automatically show final score after a short delay
                            if (currentIndex >= words.length - 1) {
                              setTimeout(() => {
                                setShowFinalScore(true);
                                // Stop voice recognition when done
                                if (recognitionRef.current && recognizing) {
                                  recognitionRef.current.stop();
                                  setKeepVoiceOn(false);
                                }
                              }, 1500); // Show results after 1.5 seconds so they can see the result
                            } else {
                              // After checking, focus Next Word button
                              setTimeout(() => {
                                nextButtonRef.current?.focus();
                              }, 100);
                            }
                          }}
                          className="bg-primary text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-primary/90 transition-colors"
                        >
                          ✓ Check This
                        </button>
                        <button
                          onClick={() => {
                            setVoicePreview(null);
                            setVoiceStatus('');
                          }}
                          className="bg-gray-200 text-gray-700 px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-300 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {result && (
                  <div className={`mt-4 p-4 rounded-lg font-semibold ${
                    result.isCorrect 
                      ? 'bg-green-50 text-green-700 border border-green-200' 
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    {result.message}
                  </div>
                )}

                <p className="mt-6 text-sm text-gray-500">
                  Voice spelling uses your browser's speech recognition (best in Chrome). Say each letter clearly:
                  "c a t" — we'll do our best to match it, but typing will always be more accurate.
                </p>

                {!recognitionRef.current && (
                  <p className="mt-4 text-sm text-red-600">
                    Voice spelling is not supported in this browser. Use Chrome on desktop for best results.
                  </p>
                )}
              </div>
            )}
          </div>

                <div className="mt-6 text-center text-sm text-gray-500">
                  Refresh the page to load a different word list.
                </div>
                
                <div className="mt-4 text-center text-xs text-gray-400">
                  This game may contain errors and is intended for entertainment purposes only.
                </div>
        </div>
      </main>
    </div>
  );
}

